import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { Search, Calendar, User, Tag, Star, Sparkles, Clock, Menu, X, ArrowRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import PageTitle from '@/components/PageTitle';
import { Filter } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

interface Case {
  id: string;
  title: string;
  description: string;
  category: string;
  beforeImage: string;
  afterImage: string;
  date: Date;
  treatment: string;
  result: string;
  patientName: string;
  doctor: string;
  duration: string;
  rating?: number;
}

interface WebsiteSettings {
  clinic: {
    name: string;
    description: string;
    address: string;
    phone: string;
    whatsapp: string;
    email: string;
    workingHours: string;
    headerImage: string;
    logo: string;
    pageTitle: string;
  };
  doctor: {
    name: string;
    title: string;
    about: string;
    image: string;
    qualifications: string[];
    statistics: {
      experienceYears: number;
      successfulCases: number;
      satisfactionRate: number;
    };
  };
  social: {
    facebook: string;
    instagram: string;
    twitter: string;
    youtube: string;
  };
}

const Cases = () => {
  const [cases, setCases] = useState<Case[]>([]);
  const [filteredCases, setFilteredCases] = useState<Case[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch website settings
        const settingsRef = doc(db, 'settings', 'website');
        const settingsDoc = await getDoc(settingsRef);
        if (settingsDoc.exists()) {
          setSettings(settingsDoc.data() as WebsiteSettings);
        }

        // Fetch cases from Firestore
        const casesRef = collection(db, 'cases');
        const q = query(casesRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const casesData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          // Safely handle createdAt field
          const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date();
          return {
            id: doc.id,
            ...data,
            date: createdAt
          };
        }) as Case[];

        console.log('Fetched cases:', casesData); // Debug log
        setCases(casesData);
        setFilteredCases(casesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          variant: 'destructive',
          title: 'خطأ في تحميل البيانات',
          description: 'حدث خطأ أثناء تحميل البيانات، يرجى المحاولة مرة أخرى',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  useEffect(() => {
    let filtered = cases;

    if (searchQuery) {
      filtered = filtered.filter(case_ =>
        case_.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        case_.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        case_.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(case_ => case_.category === selectedCategory);
    }

    setFilteredCases(filtered);
  }, [searchQuery, selectedCategory, cases]);

  const categories = ['all', ...new Set(cases.map(case_ => case_.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <PageTitle 
        title={settings?.clinic.name}
        description="تعرف على أحدث حالاتنا الناجحة في مجال طب الأسنان"
        image={settings?.clinic.headerImage}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="ابحث عن حالة..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10"
              />
            </div>
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="اختر التصنيف" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category === 'all' ? 'جميع التصنيفات' : category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCases.map((case_) => (
            <Card 
              key={case_.id} 
              className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02] bg-white/50 backdrop-blur-sm border-0"
            >
              <CardContent className="p-6">
                {/* Category Badge */}
                <div className="mb-4 flex items-center gap-2 bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-2 rounded-full w-fit">
                  <Tag className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">{case_.category}</span>
                </div>

                {/* Images Container */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Before Image */}
                  <div className="relative aspect-square overflow-hidden rounded-xl shadow-lg">
                    <img
                      src={case_.beforeImage}
                      alt={`${case_.title} - قبل`}
                      className="w-full h-full object-contain bg-gray-50/50"
                    />
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-primary text-sm px-3 py-1.5 rounded-full font-medium shadow-sm">
                      قبل
                    </div>
                  </div>

                  {/* After Image */}
                  <div className="relative aspect-square overflow-hidden rounded-xl shadow-lg">
                    <img
                      src={case_.afterImage}
                      alt={`${case_.title} - بعد`}
                      className="w-full h-full object-contain bg-gray-50/50"
                    />
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-primary text-sm px-3 py-1.5 rounded-full font-medium shadow-sm">
                      بعد
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredCases.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">لا توجد حالات متاحة</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cases;